import gtm from '@/utils/gtm';
import { useGlobal } from '@/utils/useGlobal';
import EditorLink from 'lowcode/components/EditorLink';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import { fetchPost } from 'lowcode/utils/service/FetchClient';
import { useRouter } from 'next/router';
import { useState } from 'react';
import styles from './index.module.scss';
import EcoImg from 'eco-design/base/eco-img';
import { isStatusB } from '../../../../pageStatus';
import { subscribeCookie } from 'eco-design-reliance/cookies/subscribeCookie';

export default function Subscribe({
    getSubscribeData,
    activeProduct,
}: {
    getSubscribeData: () => { sector_name: string; entry_position: number };
    activeProduct: any;
}) {
    const { useLang } = useGlobal();
    const { t } = useLang('Explore');
    const { locale = 'us' } = useRouter();
    const [input, setInput] = useState<string>('');
    const [tip, setTip] = useState<string>('');
    const [isAgreed, setIsAgreed] = useState(false);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleFocus = () => {
        if (tip) setTip('');
    };

    const handleBlur = () => {
        const email = input.trim();
        if (!email) {
            setTip(t('subscribe_empty'));
            return;
        }
        const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regex.test(email)) {
            setTip(t('subscribe_error'));
            return;
        }
        if (!isAgreed) {
            setTip(t('subscribe_check'));
            return;
        }
        return true;
    };

    const variantMap = {
        2: ['51564168347991', '51728609870163', '56791585128829', '51242023518555', '55260319744376', '51242023977307', '51820095340820'],
        3: ['51564168380759', '51728609902931', '56791585161597', '51242023551323', '55260319777144', '51242024010075', '51820095373588'],
        4: ['51564168413527', '51728609935699', '56791585194365', '51242023584091', '55260319809912', '51242024042843', '51820095406356'],
        20: ['54553979682889', '52734893490547'],
        21: ['54553980043337', '52734893523315'],
        22: ['54553980141641', '52734893556083'],
    };

    const valueMap: any = {};
    Object.keys(variantMap).forEach((key) => {
        variantMap[key as unknown as keyof typeof variantMap].forEach((item: string) => {
            valueMap[item] = key;
        });
    });

    const handleSearch = async () => {
        if (!handleBlur()) return;

        const email = input.trim();
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const configs = {
            us: {
                contactListId: ['1777574025'],
                fields: [
                    { id: '4423', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: false },
                    { id: '11150', type: 'multiple', value: [20, 21, 22], overwrite: false },
                    { id: '13030', type: 'text', value: dateString, overwrite: false },
                ],
            },
            ca: {
                contactListId: ['1264568042'],
                fields: [
                    { id: '4424', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: false },
                    { id: '11150', type: 'multiple', value: [20, 21, 22], overwrite: false },
                    { id: '13030', type: 'text', value: dateString, overwrite: false },
                ],
            },
            eu: {
                contactListId: ['1547528001'],
                fields: [
                    { id: '4427', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            uk: {
                contactListId: ['1846606487'],
                fields: [
                    { id: '4430', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            de: {
                contactListId: ['1931025920'],
                fields: [
                    { id: '4425', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            fr: {
                contactListId: ['1607239600'],
                fields: [
                    { id: '4428', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            it: {
                contactListId: ['1844328241'],
                fields: [
                    { id: '4429', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            es: {
                contactListId: ['1938148295'],
                fields: [
                    { id: '4426', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
            nl: {
                contactListId: ['1824110411'],
                fields: [
                    { id: '13668', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '13629', type: 'multiple', value: [2, 3, 4], overwrite: true },
                    { id: '13619', type: 'text', value: dateString, overwrite: false },
                ],
            },
        };
        const { contactListId, fields } = configs[locale as keyof typeof configs] || configs['us'];

        console.log(activeProduct);

        fields[2].value = [valueMap[activeProduct.variantId]];
        const res = await fetchPost(
            `${baseUrl(locale)}/subscribe/new`,
            locale,
            {
                contactListId: contactListId[0],
                email,
                fields,
                source: 'WEB_NEW_PRODUCT_SUB',
                otherContactListIds: contactListId.slice(1),
                eventTracking: subscribeCookie(getSubscribeData()),
            },
            {
                headers: {
                    // apiKey: '527760f6df93c0d6e98609fa8e8a1f4a',
                },
            },
        );

        if (res?.code === '0') {
            setTip(t('subscribe_success'));
            setInput('');
            setTimeout(() => {
                setTip('');
            }, 3000);
        } else {
            setTip(t('subscribe_fail'));
            console.error(res?.message);
        }

        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_submit',
            button_name: t('modal_btn'),
            status: res?.code === '0' ? 'success' : 'fail',
            tag: 'Agree',
            product_name: 'EcoFlow TRAIL Plus 300 DC',
        });
    };

    const statusB = isStatusB();

    return (
        <div className={`page-subscribe ${styles.Subscribe}`}>
            <div className="model-title">{statusB ? t('0920_modal_title') : t('modal_title')}</div>
            {!['nl'].includes(locale) && (
                <div className="content-container">
                    <div className="content-wrap">
                        <div className="content">
                            {statusB && <div className="model-item-title">{t('0920_modal_item1')}</div>}
                            <div className="img-wrapper">
                                <EcoImg
                                    pc={
                                        statusB
                                            ? 'https://websiteoss.ecoflow.com/figma-uploads/1757474738426-ysrjga-compressed-image%205156040.jpg'
                                            : locale === 'it'
                                            ? 'https://websiteoss.ecoflow.com/figma-uploads/1756803539587-pne1pd-compressed-coin%204.png'
                                            : 'https://websiteoss.ecoflow.com/figma-uploads/1756792726731-woombw-compressed-TRAIL-200DC_3.png'
                                    }
                                />
                            </div>

                            <div className="content-title">
                                {t.rich(statusB ? '0920_modal_item2' : 'modal_item2', {
                                    span: (tx: string) => {
                                        return <span className="yellow">{tx}</span>;
                                    },
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* 订阅输入信息 */}
            <div className="search-container">
                <input
                    type="text"
                    placeholder={t('modal_input_placeholder')}
                    className="search-input"
                    value={input}
                    onChange={handleInput}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                <button className={`search-button ${input && 'active'}`} onClick={handleSearch}>
                    {t('modal_btn')}
                </button>
            </div>
            <div className="agreement-box">
                <input type="checkbox" id="agreement" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} />
                <div className="agreement">
                    {t.rich('modal_tip', {
                        link1: (tx: string) => {
                            const link = {
                                us: 'https://account.ecoflow.com/agreement/en-us/TermsOfUse.html',
                                ca: 'https://account.ecoflow.com/agreement/en-ca/TermsOfUse.html',
                                eu: 'https://account.ecoflow.com/agreement/en-eu/TermsOfUse.html',
                                uk: 'https://account.ecoflow.com/agreement/en-eu/TermsOfUse.html',
                                de: 'https://account.ecoflow.com/agreement/de-de/TermsOfUse.html',
                                fr: 'https://account.ecoflow.com/agreement/fr-fr/TermsOfUse.html',
                                it: 'https://account.ecoflow.com/agreement/it-it/TermsOfUse.html',
                                es: 'https://account.ecoflow.com/agreement/es-es/TermsOfUse.html',
                                nl: 'https://account.ecoflow.com/agreement/nl-nl/TermsOfUse.html',
                            };

                            return (
                                <EditorLink href={link[locale as keyof typeof link]} className="agreement-link" target="_blank">
                                    {tx}
                                </EditorLink>
                            );
                        },
                        link2: (tx: string) => {
                            const link = {
                                us: 'https://account.ecoflow.com/agreement/en-us/PrivacyPolicy.html',
                                ca: 'https://account.ecoflow.com/agreement/en-ca/PrivacyPolicy.html',
                                eu: 'https://account.ecoflow.com/agreement/en-eu/PrivacyPolicy.html',
                                uk: 'https://account.ecoflow.com/agreement/en-eu/PrivacyPolicy.html',
                                de: 'https://account.ecoflow.com/agreement/de-de/PrivacyPolicy.html',
                                fr: 'https://account.ecoflow.com/agreement/fr-fr/PrivacyPolicy.html',
                                it: 'https://account.ecoflow.com/agreement/it-it/PrivacyPolicy.html',
                                es: 'https://account.ecoflow.com/agreement/es-es/PrivacyPolicy.html',
                                nl: 'https://account.ecoflow.com/agreement/nl-nl/PrivacyPolicy.html',
                            };

                            return (
                                <EditorLink href={link[locale as keyof typeof link]} className="agreement-link" target="_blank">
                                    {tx}
                                </EditorLink>
                            );
                        },
                    })}
                </div>
            </div>
            {tip && <p className={`tip${t('subscribe_success') === tip ? ' success' : ''}`}>{tip}</p>}
        </div>
    );
}
